import ChatIcon from '@mui/icons-material/Chat';
import SendIcon from '@mui/icons-material/Send';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import CloseIcon from '@mui/icons-material/Close';
import { Avatar, Box, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField, Typography } from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { USER_FILES_URL } from 'src/api_endpoints/API_URL';
import { deleteReplyOnCommentApi, getCommentRepliesByCommentIdApi, postReplyToCommentApi, toggleReplyReactionApi } from 'src/api_endpoints/userDashboard/ScamAPI';
import ConfirmationModal from './ConfirmationModal';
import { useNavigate } from 'react-router-dom';

const ViewCommentsModal = ({ open, handleClose, handleCreateComment, scamId,scamCode, commentData = [], handleToggleLikeInParent ,isPreScam = false  }) => {
  // console.log(commentData)
  const { userID } = useSelector((state) => state.userState);
  const commentsEndRef = useRef(null);
  const inputRef = useRef(null);
  const [comment, setComment] = useState('');
  const [error, setError] = useState(false);
  const [replyInputs, setReplyInputs] = useState({});
  const [replyData, setReplyData] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [replyToDelete, setReplyToDelete] = useState(null); // To store the reply being deleted
  const [replyIndexToDelete, setReplyIndexToDelete] = useState(null);
  const navigate=useNavigate()

  // Function to trigger the modal and set the reply to be deleted
  const handleDeleteClick = (index, reply) => {
    setReplyToDelete(reply);
    setReplyIndexToDelete(index);
    setIsModalOpen(true); // Open the confirmation modal
  };

  useEffect(() => {
    setTimeout(() => {
      if (commentsEndRef.current) {
        commentsEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 50);
  }, [open, commentData]);

  const handleCommentChange = (e) => {
    setComment(e.target.value);
    if (e.target.value.trim() !== '') {
      setError(false);
    }
  };

  const handleSendClick = () => {
    if (comment.trim() === '') {
      setError(true);
      return;
    }
    handleCreateComment(comment, scamId);
    setComment('');
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const toggleReplyInput = (index) => {
    setReplyInputs((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const handleReplyChange = (index, value) => {
    setReplyData((prev) => ({ ...prev, [index]: value }));
  };

  // fetch and update replies for a specific comment
  const fetchAndUpdateReplies = async (index, scam_comment_id) => {
    try {
      const updatedReplies = await getCommentRepliesByCommentIdApi({ scam_comment_id, user_id: userID });
      const updatedCommentData = [...commentData];
      updatedCommentData[index].replies = updatedReplies.replies;
      setReplyData((prev) => ({ ...prev, [index]: '' }));
    } catch (error) {
      console.error('Error fetching and updating replies:', error);
    }
  };

  const handleReplySend = async (index, scam_comment_id) => {
    if(isPreScam){
      navigate('/login')
    }
    if (!replyData[index]?.trim()) return;

    const payload = {
      scam_comment_id,
      user_id: userID,
      reply_comment: replyData[index],
    };
    try {
      const response = await postReplyToCommentApi(payload);

      if (response.success) {
        await fetchAndUpdateReplies(index, scam_comment_id);
      } else {
        console.error('Failed to post reply:', response);
      }
    } catch (error) {
      console.error('Error posting reply:', error);
    }
  };

  const removeReply = async (index, reply) => {
    const { scam_comment_id, id: reply_id } = reply;

    const payload = {
      reply_id,
      user_id: userID,
    };

    try {
      const response = await deleteReplyOnCommentApi(payload);

      setIsModalOpen(false);
      if (response.success) {
        await fetchAndUpdateReplies(index, scam_comment_id);
      } else {
        console.error('Failed to delete reply:', response);
      }
    } catch (error) {
      console.error('Error deleting reply:', error);
    }
  };

  const handleToggleLike = async (index, scam_comment_id, isLikedComment, totalLikesOnReplyComment) => {
    if(isPreScam){
      navigate('/login')
    }
    try {
      // Call the API to toggle the like reaction
      const response = await toggleReplyReactionApi({
        reply_id: scam_comment_id, // The reply's ID
        user_id: userID,           // The ID of the user interacting
      });

      if (response.success) {
        handleToggleLikeInParent(index, scam_comment_id, isLikedComment, totalLikesOnReplyComment);
      } else {
        console.error('Failed to toggle reaction', response.message);
      }
    } catch (error) {
      console.error('Error while toggling like:', error.message);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false); 
  };
  
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <Box className='flex justify-between'>
        <DialogTitle className='pt-2 pb-1'>{scamCode}: Comments ({commentData.length})</DialogTitle>

        <IconButton size="small" className='mr-2' onClick={handleClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <DialogContent sx={{ maxHeight: '400px', overflowY: 'auto', padding: '1rem' }}>
        {commentData.length > 0 && commentData.map(({ first_name, last_name, user_avatar, date_created, comment, id: scam_comment_id, isLikedComment, totalRepliesOnComment, totalLikesOnReplyComment, replies = [] }, index) => (
          <Box key={index} sx={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', marginBottom: '.5rem', borderRadius: '.6rem', backgroundColor: '#f7f4f4', padding: '1rem 1rem 0.5rem 1rem' }}>
            <Box sx={{ display: 'flex', gap: '0.5rem' }}>
              <Avatar src={USER_FILES_URL + user_avatar} alt={first_name} />
              <Box className="flex justify-between items-baseline w-full">
                <Box sx={{ fontWeight: 'bold' }}>{first_name + ' ' + last_name}</Box>
                <Box sx={{ fontSize: '0.8rem', color: 'gray' }}>
                  {formatDistanceToNow(new Date(date_created))}
                </Box>
              </Box>
            </Box>
            <Box sx={{ paddingLeft: '3rem', marginTop: '-1.4rem' }}>{comment}</Box>
            <Box className="flex gap-1 sm:pl-8">
              <Typography className='flex items-center' sx={{fontSize: {
      xs: '.8rem', // Applies to xs (extra-small) and sm (small) screens
      sm: '.8rem', // Ensures consistency for sm screens
      md: '1rem',  // Applies 1rem for md (medium) and larger screens
    }}}>
                <IconButton onClick={() => handleToggleLike(index, scam_comment_id, isLikedComment, totalLikesOnReplyComment)}>
                  {isLikedComment ? <ThumbUpIcon fontSize="small" /> : <ThumbUpOffAltIcon fontSize="small" />}
                </IconButton>
                Like {totalLikesOnReplyComment > 0 && totalLikesOnReplyComment}
              </Typography>
              <Typography className='flex items-center ' sx={{fontSize: {
      xs: '.8rem', // Applies to xs (extra-small) and sm (small) screens
      sm: '.8rem', // Ensures consistency for sm screens
      md: '1rem',  // Applies 1rem for md (medium) and larger screens
    }}}>
                <IconButton onClick={() => toggleReplyInput(index)}>
                  <ChatIcon fontSize="small" />
                </IconButton>
                Reply
              </Typography>
              {totalRepliesOnComment > 0 && (
                <Box className="flex items-center pl-1">
                  | <Typography sx={{
    color: '#8181fb',
    paddingLeft: '.5rem',
    fontSize: {
      xs: '.8rem', // Applies to xs (extra-small) and sm (small) screens
      sm: '.8rem', // Ensures consistency for sm screens
      md: '1rem',  // Applies 1rem for md (medium) and larger screens
    },
  }}>
                    {totalRepliesOnComment} {totalRepliesOnComment === 1 ? 'Reaction' : 'Reactions'}
                  </Typography>
                </Box>
              )}
            </Box>
            {replyInputs[index] && (
              <Box className="pl-8 mb-2 flex gap-2">
                <TextField
                  size="small"
                  placeholder="Write a reply..."
                  value={replyData[index] || ''}
                  onChange={(e) => handleReplyChange(index, e.target.value)}
                  fullWidth
                />
                <IconButton color="primary" onClick={() => handleReplySend(index, scam_comment_id)}>
                  <SendIcon />
                </IconButton>
              </Box>
            )}

            {/* Display first 2 replies */}
            {replies?.slice(0, 2).map((reply, replyIndex) => (
              <Box key={replyIndex} sx={{ paddingLeft: '4rem', backgroundColor: '#e9e9e9', padding: '.5rem 1rem', borderRadius: '.4rem', position: 'relative' }}>

                <Box className='flex gap-2'>
                  <Avatar src={USER_FILES_URL + reply.user_avatar} alt={first_name}
                    sx={{ width: 30, height: 30 }} />
                  <Box>
                    <Typography sx={{ fontWeight: 'bold' }}>{reply.first_name + ' ' + reply.last_name}</Typography>
                    <Typography>{reply.reply_comment}</Typography>
                  </Box>
                </Box>
                {userID === reply.user_id && (
                  <IconButton size="small" sx={{ position: 'absolute', top: 1, right: 1 }} onClick={() => handleDeleteClick(index, reply)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ))}

            {/* Show More Button */}
            {replies?.length > 2 && !replyInputs[`showAll_${index}`] && (
              <Typography
                sx={{ paddingLeft: '4rem', color: '#8181fb', cursor: 'pointer', fontWeight: 'bold', marginTop: '.5rem' }}
                onClick={() => setReplyInputs((prev) => ({ ...prev, [`showAll_${index}`]: true }))}
              >
                Show More
              </Typography>
            )}

            {/* Show all replies when "Show More" is clicked */}
            {replyInputs[`showAll_${index}`] && replies.slice(2).map((reply, replyIndex) => (
              <Box key={replyIndex} sx={{ paddingLeft: '4rem', backgroundColor: '#e9e9e9', padding: '.5rem 1rem', borderRadius: '.4rem', position: 'relative' }}>

              <Box className='flex gap-2'>
                <Avatar src={USER_FILES_URL + reply.user_avatar} alt={first_name}
                  sx={{ width: 30, height: 30 }} />
                <Box>
                  <Typography sx={{ fontWeight: 'bold' }}>{reply.first_name + ' ' + reply.last_name}</Typography>
                  <Typography>{reply.reply_comment}</Typography>
                </Box>
              </Box>
              {userID === reply.user_id && (
                <IconButton size="small" sx={{ position: 'absolute', top: 1, right: 1 }} onClick={() => handleDeleteClick(index, reply)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
            ))}

          </Box>
        ))}
        <div ref={commentsEndRef} />
      </DialogContent>
      <DialogActions sx={{ padding: '1rem' }}>
        <TextField
          fullWidth
          placeholder="Write a comment..."
          value={comment}
          onChange={handleCommentChange}
          error={error}
          inputRef={inputRef}
        />
        <IconButton color="primary" onClick={handleSendClick}>
          <SendIcon />
        </IconButton>
      </DialogActions>

      {isModalOpen && (
        <ConfirmationModal
          text="Are you sure you want to delete this reply?"
          onConfirm={() => removeReply(replyIndexToDelete, replyToDelete)} // Confirm deletion
          onCancel={handleCancel} // Close modal on cancel
        />
      )}
    </Dialog>
  );
};

export default ViewCommentsModal;
